import React, { useCallback, useMemo, useRef } from 'react';
import { useDisplayCurrency } from '@features/currency/selected-display-currency.hook';
import { useElementListGroupBarScale } from '@features/proposals/use-element-list-group-bar-scale.hooks';
import { useItemsSelectedProposalColor } from '@features/proposals/hooks/use-proposals.hooks';
import { useSelectedProposalId } from '@client/hooks/use-selected-proposal-id.hooks';
import {
  useGetResultsPerGFA,
  useVersionResultRecord,
} from '@features/results/use-results.hooks';
import {
  attachBaselineCo2BehindProposalBar,
  buildProposalBarEntry,
  getBaselineCo2ValuePerGfaForItemIds,
} from './proposal-bar.helpers';
import type { SortedElementGroupSection } from '@client/pages/MainView/components/ProjectView/components/ElementsView/use-sorted-element-groups.hook';
import { projectGuards } from '@shared/helpers/guards/project-guards.helpers';
import { filterListElementsContributingToVersionTotals } from '@shared/helpers/element-list-group-contributors.helpers';
import {
  useExpandedItems,
  setElementExpanded,
} from '@client/hooks/use-expand-elements.hooks';
import { useAddElement, getProject } from '@client/store/project';
import { getUIState } from '@client/store/ui';
import { useUpdateProject } from '@client/store/project';
import { updateBuildingVersionProperties } from '@shared/helpers/project.helpers';
import { moveElements } from '@client/hooks/use-move-elements.hooks';
import { useMoveElements } from '@client/hooks/use-move-elements.hooks';
import { setSortElementsBy } from '@client/hooks/use-filter-elements.hooks';
import Droppable, { DropType } from '@ui/drag-and-drop/Droppable';
import {
  getSelectedElementId,
  getSelectedVersion,
  useSelectedVersion,
} from '@client/store/ui';
import { useProjectNavigation } from '@client/hooks/use-router.hooks';
import { URL_CATEGORY_NONE } from '@client/models/router.model';
import { hasElementSelection } from '@features/proposals/helpers/proposal-selection.helpers';
import { ElementKind } from '@shared/models/project.model';
import {
  closeMenu,
  menuMouseHandler,
  openMenu,
  useIsMenuOpen,
  useMenuPosition,
} from '@client/hooks/use-menu.hooks';
import { Menu } from '@ui/menus/Menu';
import { Icon } from '@ui/Icon';
import { useGroupedElementCategories } from '@features/elements/hooks/use-grouped-element-categories.hook';
import { ElementCategoryMenuContent } from '@features/elements/components/ElementCategoryMenu/ElementCategoryMenuContent';
import { ElementCategoryID } from '@shared/models/element-categories.model';
import { setElementProperties } from '@shared/helpers/element-property.helpers';
import { DEFAULT_SBEF_CUSTOM_GROUPS } from '@features/sbef/custom-groups.model';
import { newElementOptionsForSbefGroupAdd } from './sbef-group-add.helpers';
import {
  getSbefGroupDropCode,
  getSbefGroupDropPropertyChange,
} from './sbef-group-drop.helpers';
import { useOpenFocusGroup } from '@client/hooks/use-element-focus.hook';
import { HideOutsideViewport } from '@ui/HideOutsideViewport';
import ElementListRow, {
  ELEMENT_LIST_ROW_HEIGHT,
  ElementListRowHandle,
  ProposalBar,
} from './ElementListRow';
import ElementTreeRow from './ElementTreeRow';
import { useResolvedVersion } from '@client/hooks/use-resolved-version.hooks';

type SbefGroupRowProps = {
  group: SortedElementGroupSection;
  selectedRow: string | null;
  onSelect: (id: string) => void;
  hideDivider?: boolean;
  forceExpandedElementIds?: ReadonlySet<string>;
  forceExpandedGroupKeys?: ReadonlySet<string>;
  startDepth?: number;
};

const SbefGroupRow: React.FC<SbefGroupRowProps> = ({
  group,
  selectedRow,
  onSelect,
  hideDivider,
  forceExpandedElementIds,
  forceExpandedGroupKeys,
  startDepth = 0,
}) => {
  const {
    sbefGroupBarScale: { maxCo2: co2ePerGfaMax, maxCost: costPerGfaMax },
  } = useElementListGroupBarScale();
  const getResultsPerGFA = useGetResultsPerGFA();
  const displayCurrency = useDisplayCurrency();
  const addElement = useAddElement();
  const updateProject = useUpdateProject();
  const { isMoveElementsAllowed } = useMoveElements();
  const categoryItems = useGroupedElementCategories();
  const resolvedVersion = useResolvedVersion();
  const navigateTo = useProjectNavigation();

  const groupSbefCode = useMemo(
    () => getSbefGroupDropCode(group.key, DEFAULT_SBEF_CUSTOM_GROUPS),
    [group.key],
  );

  const handleGroupDrop = useCallback(
    (
      dragId: string,
      dropId: string | undefined,
      dropType: DropType | undefined,
    ) => {
      if (!dropId || !dropType) return;
      const project = getProject();
      setSortElementsBy('position');
      getUIState().setSelectedElementId(dragId);
      if (dropType === 'inside') {
        setElementExpanded(dropId, true);
      }
      const movedProject = moveElements(project, dropId, dropType, dragId);
      const finalProject = setElementProperties(
        movedProject,
        dragId,
        getSbefGroupDropPropertyChange(groupSbefCode),
      );
      // TODO(replay): express as ProjectChange so a 422 race can be replayed.
      updateProject({ kind: 'snapshot', project: finalProject });
    },
    [groupSbefCode, updateProject],
  );

  const handleGroupDropValidate = useCallback(
    (
      dragId: string,
      dropId: string | undefined,
      dropType: DropType | undefined,
    ) => isMoveElementsAllowed(dropId, dropType, dragId),
    [isMoveElementsAllowed],
  );

  const openFocusGroup = useOpenFocusGroup();

  const rowRef = useRef<ElementListRowHandle>(null);
  const menuId = `sbef-group-${group.key}`;
  const menuPosition = useMenuPosition(menuId);
  const kebabMenuOpen = useIsMenuOpen(menuId);

  const { is: isGroupExpanded, set: setGroupExpanded } = useExpandedItems({
    defaultPrefix: 'sbef',
  });
  const forceOpen = forceExpandedGroupKeys?.has(group.key) ?? false;
  const expanded = forceOpen ? true : isGroupExpanded(group.key);

  const handleGroupHeaderDrop = useCallback(
    (dragId: string) => {
      const project = getProject();
      const groupItems = group.items.filter(projectGuards.is.element);
      const firstItem = groupItems[0];
      const version = getSelectedVersion();
      if (!firstItem && !version) return;

      setSortElementsBy('position');
      setGroupExpanded(group.key, true);
      getUIState().setSelectedElementId(dragId);

      let movedProject;
      if (firstItem) {
        movedProject = moveElements(project, firstItem.id, 'above', dragId);
      } else {
        // firstItem is absent only for an empty group; version is required to drop inside the version root
        if (!version) return;
        movedProject = moveElements(project, version.id, 'inside', dragId);
      }

      const finalProject = setElementProperties(
        movedProject,
        dragId,
        getSbefGroupDropPropertyChange(groupSbefCode),
      );
      // TODO(replay): express as ProjectChange so a 422 race can be replayed.
      updateProject({ kind: 'snapshot', project: finalProject });
    },
    [group.items, group.key, groupSbefCode, setGroupExpanded, updateProject],
  );

  const handleGroupHeaderValidate = useCallback(
    (dragId: string) => {
      const version = getSelectedVersion();
      if (!version) return false;
      // Any element in the version can be dropped on the group header
      return group.items.every((item) => item.id !== dragId);
    },
    [group.items],
  );

  // Mirror `useGroupSectionProps` / `resultsSum`: roots that contribute to version totals.
  // Group `items` are built from the resolved version tree in the elements list view.
  const itemIds = useMemo(
    () =>
      filterListElementsContributingToVersionTotals(
        group.items,
        resolvedVersion,
      )
        .filter(projectGuards.is.element)
        .map((el) => el.id),
    [group.items, resolvedVersion],
  );
  const selectedProposalId = useSelectedProposalId();
  const proposalColor = useItemsSelectedProposalColor(itemIds);
  const selectedVersion = useSelectedVersion();
  const baselineResultRecord = useVersionResultRecord(selectedVersion);

  // Use the same aggregates as {@link useGroupSectionProps} so bar values match
  // the SBEF-only scale max (baseline vs proposal active-set sums).
  const proposalBars: ProposalBar[] = useMemo(() => {
    const raw =
      selectedProposalId == null
        ? group.meta.resultsSum
        : (group.meta.proposalResultsSum[selectedProposalId] ?? {});
    const bar = buildProposalBarEntry(
      getResultsPerGFA(raw),
      proposalColor,
      displayCurrency,
    );
    // `group.meta.resultsSum` follows the proposal-resolved record when a
    // proposal is selected, so it is NOT the pre-proposal baseline.
    return [
      attachBaselineCo2BehindProposalBar(bar, {
        selectedProposalId,
        proposalColor,
        hasSelectedVersion: selectedVersion != null,
        baselineCo2Value: getBaselineCo2ValuePerGfaForItemIds(
          itemIds,
          baselineResultRecord,
          getResultsPerGFA,
        ),
      }),
    ];
  }, [
    group.meta.resultsSum,
    group.meta.proposalResultsSum,
    selectedProposalId,
    proposalColor,
    displayCurrency,
    getResultsPerGFA,
    selectedVersion,
    baselineResultRecord,
    itemIds,
  ]);

  const handleKebabClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.nativeEvent.stopPropagation();
      const anchorPosition = menuMouseHandler(e);
      if (anchorPosition) {
        openMenu({ anchorPosition, id: menuId });
      }
    },
    [menuId],
  );

  const handleAddElement = useCallback(
    async (categoryId: ElementCategoryID) => {
      const version = getSelectedVersion();
      if (!version) return;

      // Seed the group's code when the category can store it, so the row lands here
      // in one write. Maintenance has no sbef_code row, so it stays ungrouped (DEV-3962).
      await addElement(
        version,
        { kind: ElementKind.Element, category_id: categoryId },
        newElementOptionsForSbefGroupAdd(categoryId, group.key),
      );

      closeMenu();
    },
    [addElement, group.key],
  );

  const handleRename = useCallback(() => {
    closeMenu();
    rowRef.current?.startRename();
  }, []);

  const handleExpandedChange = useCallback(
    (v: boolean) => setGroupExpanded(group.key, v),
    [setGroupExpanded, group.key],
  );

  const handleGroupSelect = useCallback(() => {
    onSelect(group.key);
    if (!hasElementSelection(getSelectedElementId())) return;
    void navigateTo({ elementId: URL_CATEGORY_NONE });
  }, [navigateTo, onSelect, group.key]);

  const selected = selectedRow === group.key;
  const handleFocusClick = useCallback(
    () => openFocusGroup(group.key),
    [openFocusGroup, group.key],
  );
  const groupName = resolvedVersion?.sbefGroupNames?.[group.key];

  const handleNameSave = useCallback(
    (value: string) => {
      const version = getSelectedVersion();
      if (!version) return;

      const trimmedValue = value.trim();
      const currentName = version.sbefGroupNames?.[group.key] ?? '';
      if (trimmedValue === currentName) return;

      const nextGroupNames = { ...(version.sbefGroupNames ?? {}) };
      if (trimmedValue) nextGroupNames[group.key] = trimmedValue;
      else delete nextGroupNames[group.key];

      const nextVersionGroups =
        Object.keys(nextGroupNames).length > 0 ? nextGroupNames : undefined;
      const nextProject = updateBuildingVersionProperties(getProject(), {
        id: version.id,
        sbefGroupNames: nextVersionGroups,
      });
      // TODO(replay): express as ProjectChange so a 422 race can be replayed.
      updateProject({ kind: 'snapshot', project: nextProject });
    },
    [group.key, updateProject],
  );

  return (
    <>
      <HideOutsideViewport
        height={ELEMENT_LIST_ROW_HEIGHT}
        alwaysVisible={selected}
      >
        <Droppable
          dropId={group.key}
          allowDropInside
          onDrop={handleGroupHeaderDrop}
          validateFn={handleGroupHeaderValidate}
        >
          <ElementListRow
            ref={rowRef}
            variant="group"
            name={groupName}
            groupNumber={group.key.split('.')[0]}
            depth={startDepth}
            namePlaceholder={group.label.replace(/^[^.]+\.\s*/, '')}
            onNameSave={handleNameSave}
            expandable={group.items.length > 0 && !forceOpen}
            expanded={expanded}
            onExpandedChange={handleExpandedChange}
            proposals={proposalBars}
            maxCo2={co2ePerGfaMax > 0 ? co2ePerGfaMax : undefined}
            maxCost={costPerGfaMax > 0 ? costPerGfaMax : undefined}
            selected={selected}
            onSelect={handleGroupSelect}
            onFocusClick={selected ? handleFocusClick : undefined}
            onKebabClick={handleKebabClick}
            kebabMenuOpen={kebabMenuOpen}
            hideQuantity
            hideDivider={hideDivider}
          />
        </Droppable>
      </HideOutsideViewport>
      <Menu
        open={Boolean(menuPosition)}
        anchorPosition={menuPosition}
        onClose={closeMenu}
      >
        <Menu.Submenu
          label="Add element"
          icon={<Icon category="mui" icon="Add" />}
        >
          <ElementCategoryMenuContent
            categoryGroups={categoryItems}
            onSelect={handleAddElement}
          />
        </Menu.Submenu>
        <Menu.Item
          icon={<Icon category="mui" icon="Edit" />}
          onClick={handleRename}
        >
          Rename
        </Menu.Item>
      </Menu>
      {expanded &&
        group.visibleItems
          .filter(projectGuards.is.element)
          .map((element) => (
            <ElementTreeRow
              key={element.id}
              element={element}
              depth={startDepth + 1}
              selectedRow={selectedRow}
              onSelect={onSelect}
              forceExpandedElementIds={forceExpandedElementIds}
              onDrop={handleGroupDrop}
              validateDrop={handleGroupDropValidate}
            />
          ))}
    </>
  );
};

export default SbefGroupRow;
